import { useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { useDataCollectionSource } from '@factorialco/f0-react/dist/experimental'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Upload } from '@factorialco/f0-react/icons/app'
import type { PurchaseRequestDetailQuery } from 'generated/resources/graphql'
import { PurchaseRequestFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import DropItem from 'modules/procurement/components/DropItem'

import FileUploadModal from './FileUploadModal'

type ProcurementFile =
  | NonNullable<PurchaseRequestDetailQuery['procurement']['purchaseRequest']>['files'][number]
  | NonNullable<
      NonNullable<
        NonNullable<
          PurchaseRequestDetailQuery['procurement']['purchaseRequest']
        >['uploadDocumentTask']
      >[number]
    >['files'][number]

interface Props {
  displayDataCollection: boolean
  purchaseRequest: PurchaseRequestFragment
}

const Attachments = ({ purchaseRequest, displayDataCollection }: Props) => {
  const { company } = useCurrentSession()
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)

  type FileRecord = ProcurementFile & {
    source: 'purchaseRequest' | 'uploadDocumentTask'
    taskContent?: string | null
    taskName?: string | null
  }

  // Calculate total files count for empty state check
  const purchaseRequestFiles = purchaseRequest?.files || []
  // Collect files from all upload document tasks
  const uploadTaskFiles = (purchaseRequest.uploadDocumentTask || []).flatMap(
    (task) => task?.files || []
  )
  const totalFilesCount = purchaseRequestFiles.length + uploadTaskFiles.length

  const source = useDataCollectionSource(
    {
      dataAdapter: {
        fetchData: async (options) => {
          // Collect files from all upload document tasks with their taskContent
          const allUploadTaskFiles: FileRecord[] = (
            purchaseRequest.uploadDocumentTask || []
          ).flatMap((task) =>
            (task?.files || []).map((file) => ({
              ...file,
              source: 'uploadDocumentTask' as const,
              taskContent: task?.taskContent || null,
              taskName: task?.taskName || null,
            }))
          )
          // Merge and normalize the files
          const mergedFiles: FileRecord[] = [
            ...purchaseRequestFiles.map((file) => ({
              ...file,
              source: 'purchaseRequest' as const,
            })),
            ...allUploadTaskFiles,
          ]

          let filteredFiles = mergedFiles
          if (options.filters?.source) {
            filteredFiles = mergedFiles.filter((file) =>
              options.filters.source?.includes(file.source)
            )
          }

          return {
            records: filteredFiles,
          }
        },
      },
      filters: {
        source: {
          label: i18n.t(
            'procurement.purchase_requests.detail.attachments.data_collection.filters.source.label'
          ),
          type: 'in',
          options: {
            options: [
              {
                label: i18n.t(
                  'procurement.purchase_requests.detail.attachments.data_collection.filters.source.options.purchaseRequest'
                ),
                value: 'purchaseRequest',
              },
              {
                label: i18n.t(
                  'procurement.purchase_requests.detail.attachments.data_collection.filters.source.options.uploadDocumentTask'
                ),
                value: 'uploadDocumentTask',
              },
            ],
          },
        },
      },
      itemOnClick: (file: FileRecord) => {
        return () => {
          if (file.url) {
            window.open(file.url, '_blank', 'noopener,noreferrer')
          }
        }
      },
      secondaryActions: () => [
        {
          label: i18n.t(
            'procurement.purchase_requests.detail.attachments.data_collection.actions.upload_file'
          ),
          icon: Upload,
          variant: 'default' as const,
          onClick: () => setIsUploadModalOpen(true),
        },
      ],
    },
    [purchaseRequest.id, purchaseRequestFiles.length, uploadTaskFiles.length]
  )

  return (
    <>
      {totalFilesCount > 0 ? (
        <Box paddingTop="s24" gap="s12">
          {displayDataCollection ? (
            <OneDataCollection
              source={source}
              visualizations={[
                {
                  type: 'table',
                  options: {
                    columns: [
                      {
                        label: i18n.t(
                          'procurement.purchase_requests.detail.attachments.data_collection.columns.filename'
                        ),
                        render: (file: FileRecord) => ({
                          type: 'file' as const,
                          value: {
                            name: file.filename,
                            type: file.contentType || 'application/octet-stream',
                          },
                        }),
                      },
                      {
                        label: i18n.t(
                          'procurement.purchase_requests.detail.attachments.data_collection.columns.task_name'
                        ),
                        render: (file: FileRecord) => {
                          if (file.source === 'uploadDocumentTask') {
                            return file.taskName || ''
                          }
                          return ''
                        },
                      },
                      {
                        label: i18n.t(
                          'procurement.purchase_requests.detail.attachments.data_collection.columns.task_content'
                        ),
                        render: (file: FileRecord) => {
                          if (file.source === 'uploadDocumentTask') {
                            return file.taskContent || ''
                          }
                          return ''
                        },
                      },
                    ],
                  },
                },
              ]}
            />
          ) : (
            purchaseRequestFiles.map((file: ProcurementFile) => (
              <DropItem key={file.filename} file={file} />
            ))
          )}
        </Box>
      ) : (
        <Box
          justifyContent="center"
          alignItems="center"
          textAlign="center"
          paddingTop="s24"
          gap="s12"
        >
          <Text.Normal size="200">
            {i18n.t('procurement.purchase_requests.detail.attachments.empty')}
          </Text.Normal>
        </Box>
      )}

      <FileUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        purchaseRequest={purchaseRequest}
        companyId={company.id}
      />
    </>
  )
}

export default Attachments
